<template lang="pug">
  div
    .d-flex.justify-space-between.align-center.mb-2
      // Edit Mode
      .d-flex.align-center.gap-1(v-if="isEditMode")
        // Dropdown for type selection
        yz-dropdown(v-model="selectedType" :items="typeOptions" item-text="text" item-value="value" size="md" style="max-width: 150px")

        // Amount input field
        yz-text-field(v-if="selectedType === 'fixed'" v-model.number="amount" type="number" placeholder="0" size="md" style="max-width: 100px" :prefix="$currency.symbol" min="0" :rules="fixedAmountRules" autofocus)
        yz-text-field(v-else, v-model.number="percentageValue" type="number" placeholder="0" size="md" style="max-width: 65px" suffix="%" min="0" :rules="percentageRules" autofocus)

        // Tick button (show when has value and is valid)
        yz-btn(v-if="hasValue && isValid" icon="$yzi-check" icon-only size="sm" icon-color="yz-success-500" @click="onExitEditMode")
        // Close button
        yz-btn(icon="mdi-close" icon-only size="sm" color="yz-neutral-500" @click="onRemoveValueAndExit")

      // Read Mode
      .d-flex.align-center.gap-1(v-else)
        .yz-medium-body-sm.text-neutral-700.no-wrap(:class="[hasValue || readonly ? '' : 'underline cursor-pointer deduction-hover']" @click="onEnterEditMode") {{ $t('admin_fee') }}
        // Close button (hidden in readonly mode)
        yz-btn(v-if="hasValue && !readonly" icon="mdi-close" icon-only size="sm" color="yz-neutral-500" @click="onRemoveValue")

      .yz-medium-body-sm.text-neutral-700.no-wrap
        span(v-if="hasValue && !isEditMode") -
        span {{ adminFeeValue | displayPrice($currency) }}

</template>

<script>
import { validateDecimalPlaces } from '@/utils'

export default {
  props: {
    adminFeeValue: {
      type: Number,
      default: null
    },
    adminFeeType: {
      type: String,
      default: 'fixed',
      validator: value => ['fixed', 'percentage'].includes(value)
    },
    adminFeePercentage: {
      type: Number,
      default: null
    },
    baseAmount: {
      type: Number,
      default: 0
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      amount: null,
      percentageValue: null,
      isEditMode: false,
      selectedType: 'fixed'
    }
  },
  computed: {
    typeOptions() {
      return [
        { text: this.$t('fixed_amount'), value: 'fixed' },
        { text: this.$t('percentage'), value: 'percentage' }
      ]
    },
    hasValue() {
      if (this.selectedType === 'percentage') {
        return this.percentageValue !== null && this.percentageValue > 0
      }
      return this.amount !== null && this.amount > 0
    },
    fixedAmountRules() {
      return [v => v === null || v === '' || v >= 0 || this.$t('admin_fee_cannot_be_negative'), v => validateDecimalPlaces(v, this.$currency)]
    },
    percentageRules() {
      return [v => v === null || v === '' || (v >= 0 && v <= 100) || this.$t('percentage_must_be_between_0_and_100'), v => v === null || v === '' || Number.isInteger(Number(v)) || this.$t('percentage_must_be_integer')]
    },
    isValid() {
      if (this.selectedType === 'percentage') {
        if (this.percentageValue === null || this.percentageValue === '') return true
        return this.percentageRules.every(rule => rule(this.percentageValue) === true)
      } else {
        if (this.amount === null || this.amount === '') return true
        return this.fixedAmountRules.every(rule => rule(this.amount) === true)
      }
    }
  },
  mounted() {
    this.amount = this.adminFeeValue ? this.adminFeeValue / this.$currency.multiplier : null
    this.selectedType = this.adminFeeType || 'fixed'
    this.percentageValue = this.adminFeePercentage
  },
  watch: {
    adminFeeValue(newValue) {
      this.amount = newValue ? newValue / this.$currency.multiplier : null
    },
    adminFeePercentage(newValue) {
      this.percentageValue = newValue
    },
    adminFeeType(newValue) {
      this.selectedType = newValue || 'fixed'
    },
    percentageValue(newValue) {
      // Calculate amount based on percentage change
      if (this.selectedType === 'percentage' && newValue !== null && newValue !== '') {
        const percentageAmount = (this.baseAmount * newValue) / 100
        this.amount = percentageAmount / this.$currency.multiplier
      }
    },
    baseAmount(newValue) {
      // Recalculate admin fee when base amount changes (only for percentage type)
      if (this.selectedType === 'percentage' && this.percentageValue !== null && this.percentageValue > 0) {
        const percentageAmount = (newValue * this.percentageValue) / 100
        this.amount = percentageAmount / this.$currency.multiplier
        // Emit updated admin fee value to parent
        this.$emit('update:admin-fee-value', Math.round(percentageAmount))
      }
    }
  },
  methods: {
    onEnterEditMode() {
      if (!this.hasValue && !this.readonly) {
        this.isEditMode = true
      }
    },
    onExitEditMode() {
      if (this.selectedType === 'percentage') {
        if (this.percentageValue === null || this.percentageValue === '' || this.percentageValue <= 0) {
          this.percentageValue = null
          this.amount = null
          this.$emit('update:admin-fee-value', null)
          this.$emit('update:admin-fee-percentage', null)
        } else {
          // Calculate amount in cents from percentage
          const percentageAmount = (this.baseAmount * this.percentageValue) / 100
          this.$emit('update:admin-fee-value', Math.round(percentageAmount))
          this.$emit('update:admin-fee-percentage', this.percentageValue)
        }
      } else {
        // Handle fixed type
        if (this.amount === null || this.amount === '' || this.amount <= 0) {
          // Clear values if invalid
          this.amount = null
          this.percentageValue = null
          this.$emit('update:admin-fee-value', null)
          this.$emit('update:admin-fee-percentage', null)
        } else {
          // Convert amount to cents
          const centValue = Math.round(this.amount * this.$currency.multiplier)
          this.$emit('update:admin-fee-value', centValue)
          this.$emit('update:admin-fee-percentage', null)
        }
      }

      this.isEditMode = false
      this.$emit('update:admin-fee-type', this.selectedType)
    },
    onRemoveValue() {
      this.amount = null
      this.percentageValue = null
      this.$emit('update:admin-fee-value', null)
      this.$emit('update:admin-fee-percentage', null)
    },
    onRemoveValueAndExit() {
      this.onRemoveValue()
      this.isEditMode = false
    }
  }
}
</script>

<style lang="scss" scoped>
.deduction-hover {
  transition: background-color 0.2s ease;

  &:hover {
    background-color: var(--yz-neutral-300);
    border-radius: 4px;
  }
}
</style>
